/* eslint-disable max-lines */
import {appStoreBadge, googlePlayBadge} from '@gaia/images/badges';
import {gaiaWhiteTextCropped} from '@gaia/images/logos';
import gettext from '@gaia/utils/django/abstractGettext';
import trackAnalytics from '@gaia/utils/gaia/trackAnalytics';
import once from 'lodash/once';
import {useEffect} from 'react';

import createRoute from './createRoute-500w.jpg';
import hiking from './hiking-500w.jpg';
import printMap from './printMap-500w.jpg';

import '@gaia/scss/mobile-map.scss';
import './MobileModal.css';

function trackGetTheAppClick() {
    // @TODO: fix trackAnalytics (below also)
    // @ts-expect-error
    trackAnalytics('Click Get The App Button', {category: 'Commerce', label: 'Main Map'});
}

function trackAppStoreClick() {
    trackAnalytics('Select Download iOS App', {category: 'Commerce', label: 'Main Map'});
}

function trackGooglePlayClick() {
    trackAnalytics('Select Download Android App', {category: 'Commerce', label: 'Main Map'});
}

const trackPageView = once(() =>
    // @ts-expect-error
    trackAnalytics('View Mobile Map Modal', {url: window.location.href})
);

export function MobileModal({dismissModal}: {dismissModal: () => void}) {
    useEffect(() => trackPageView(), []);

    return (
        <div id='mobileMap' className='mobile-map-modal'>
            <div className='mobile-map-modal-content'>
                <div id='mobile-map-modal-close' className='mobile-map-modal-close-area'>
                    <button
                        className='mobile-map-modal-close'
                        aria-label='Close'
                        onClick={dismissModal}
                    >
                        <span aria-hidden='true'>&times;</span>
                    </button>
                </div>
                <div className='main-intro cover-image'>
                    <img
                        style={{display: 'block'}}
                        height='44'
                        width='175'
                        alt='Gaia GPS - Trail maps for every adventure'
                        src={gaiaWhiteTextCropped}
                    />
                    <div className='section-content'>
                        <h1>
                            {gettext(
                                navigator.language ?? 'en',
                                'Topo Maps and Satellite Imagery for Every Adventure'
                            )}
                        </h1>
                        <p>
                            {gettext(
                                navigator.language ?? 'en',
                                'Access free topo maps and satellite imagery on your computer and mobile device with Gaia GPS. Navigate in the backcountry with the Gaia GPS app for iOS and Android mobile devices. Start planning your next adventure with print maps, route planning tools, satellite imagery, and other specialty trail maps.'
                            )}
                        </p>

                        <div style={{marginTop: '8px'}}>
                            <a
                                href='https://itunes.apple.com/us/app/gaia-gps/id1201979492?mt=8&uo=4&at=10lqtF&ct=Main%20Map'
                                onClick={() => trackAppStoreClick()}
                                target='itunes_store'
                                className='hide-if-android'
                                style={{marginLeft: '4px', marginRight: '4px', marginBottom: '8px'}}
                            >
                                <img
                                    src={appStoreBadge}
                                    height='52'
                                    alt='Download on the App Store'
                                    style={{verticalAlign: 'middle'}}
                                />
                            </a>

                            <a
                                href='https://market.android.com/details?id=com.trailbehind.android.gaiagps.pro&hl=en'
                                onClick={() => trackGooglePlayClick()}
                                className='hide-if-ios'
                                style={{marginLeft: '4px', marginRight: '4px', marginBottom: '8px'}}
                            >
                                <img
                                    src={googlePlayBadge}
                                    height='52'
                                    alt='Get it on Google Play'
                                    style={{verticalAlign: 'middle'}}
                                />
                            </a>
                        </div>
                    </div>
                </div>
                <div className='mobile-map-sub-header'>
                    <div style={{margin: '50px 0'}}>
                        <h2>Get the Best Trail Maps</h2>
                        <p>
                            Download satellite and topo maps to use without cell service. Gaia GPS
                            offers an extensive map catalog with speciality maps to help you get out
                            on the trail.
                        </p>
                        <h4>Preview Available Maps:</h4>
                        <div className='mobile-map-modal-list'>
                            <ul className='mobile-list-left'>
                                <li>
                                    <a href='/maps/topo/'>Topo maps</a>
                                </li>
                                <li>
                                    <a href='/maps/satellite/'>Satellite maps</a>
                                </li>
                                <li>
                                    <a href='/maps/road/'>Road maps</a>
                                </li>
                                <li>
                                    <a href='/maps/feature-weather/'>
                                        Features and weather overlays
                                    </a>
                                </li>
                                <li>
                                    <a href='/maps/united-states/'>Maps for the US</a>
                                </li>
                                <li>
                                    <a href='/maps/canada/'>Maps for Canada</a>
                                </li>
                                <li>
                                    <a href='/maps/europe/'>Maps for Europe</a>
                                </li>
                                <li>
                                    <a href='/maps/australia-new-zealand-asia/'>
                                        Maps for Australia, New Zealand, and Asia
                                    </a>
                                </li>
                                <li>
                                    <a href='/maps/historic/'>Historical maps</a>
                                </li>
                            </ul>
                            <ul className='mobile-list-right'>
                                <li>
                                    <a href='/maps/nautical-aviation/'>
                                        Nautical and Aviation maps
                                    </a>
                                </li>
                                <li>
                                    <a href='/maps/hiking/'>Hiking Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/backpacking/'>Backpacking Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/offroad/'>Offroading Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/mountain-bike/'>Biking Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/fishing/'>Fishing Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/camping/'>Camping Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/backcountry-ski/'>Backcountry Ski Maps</a>
                                </li>
                                <li>
                                    <a href='/maps/hunting/'>Hunting</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div style={{marginBottom: '50px'}}>
                        <div>
                            <img
                                className='responsive-img'
                                src={hiking}
                                loading='lazy'
                                height='250'
                                width='250'
                                alt='Man hiking and the Gaia GPS app.'
                                style={{boxShadow: '0 1px 4px'}}
                            />
                        </div>
                        <div style={{margin: '50px 0'}}>
                            <h2>Discover New Parks and Forests</h2>
                            <p>Explore parks and find a trail to fit your adventure needs.</p>
                            <div className='mobile-map-modal-list'>
                                <ul className='mobile-list-left'>
                                    <li>
                                        <a href='/hike/united-states/new-york/allegany-state-park/'>
                                            Allegany State-Park
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/virginia/george-washington-national-forest/'>
                                            George Washington National Forest
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/georgia/chattahoochee-oconee-national-forest-blue-ridge-district/'>
                                            Chattahoochee-Oconee National Forest
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/park/great-smoky-mountains-national-park/'>
                                            Great Smoky Mountains National Park
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/colorado/gunnison-national-forest/'>
                                            Gunnison National Forest
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/utah/grand-staircase-escalante-national-monument/'>
                                            Grand Staircase Escalante National Monument
                                        </a>
                                    </li>
                                </ul>
                                <ul className='mobile-list-right'>
                                    <li>
                                        <a href='/hike/united-states/california/redwood-national-park/'>
                                            Redwood National Park
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/california/kings-canyon-national-park/'>
                                            Kings Canyon National Park
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/new-mexico/santa-fe-national-forest/'>
                                            Santa Fe National Forest
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/wyoming/grand-teton-national-park/'>
                                            Grand Teton National Park
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/idaho/sawtooth-national-forest/'>
                                            Sawtooth National Forest
                                        </a>
                                    </li>
                                    <li>
                                        <a href='/hike/united-states/oregon/mount-jefferson-wilderness/'>
                                            Mount Jefferson Wilderness
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div style={{margin: '50px 0'}}>
                        <div>
                            <img
                                className='responsive-img'
                                src={printMap}
                                loading='lazy'
                                height='166'
                                width='250'
                                alt='Woman looking at a printed map in nature.'
                                style={{boxShadow: '0 1px 4px'}}
                            />
                        </div>
                        <div style={{margin: '50px 0'}}>
                            <h2>Print Maps and Carry a Backup</h2>
                            <p>
                                Print off trail maps on <a href='/'>gaiagps.com</a> and carry a
                                paper backup map into the backcountry in case your phone gets lost
                                or damaged. Learn more about{' '}
                                <a href='https://blog.gaiagps.com/print-maps-online/'>
                                    how to print maps on your desktop computer.
                                </a>
                            </p>
                        </div>
                    </div>

                    <div style={{marginBottom: '50px'}}>
                        <div>
                            <img
                                className='responsive-img'
                                src={createRoute}
                                loading='lazy'
                                height='250'
                                width='250'
                                alt='A route being measured on a map in the Gaia GPS app.'
                            />
                        </div>
                        <div style={{margin: '50px 0'}}>
                            <h2>Plan Hiking and Offroading Routes and More</h2>
                            <p style={{paddingBottom: '30px'}}>
                                Quickly find the best trails and roads to your backcountry
                                destination with route planning tools that snap to known trails on
                                iOS, Android, and the web. View distance and elevation changes so
                                you know what&apos;s coming in the miles ahead.
                            </p>
                            <a
                                className='ga-button-primary ga-button-large'
                                href='/download-app-redirect/'
                                onClick={() => trackGetTheAppClick()}
                            >
                                Get the App
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
